import React, { useState } from 'react';
import { Heart, FileCheck, AlertTriangle, Save, Loader2 } from 'lucide-react';
import { api } from '../../services/apiClient';

interface Props {
  application: any;
  onSaved: () => void;
}

const DISABILITY_LEVELS = [
  { value: 'znaczny', label: 'Znaczny' },
  { value: 'umiarkowany', label: 'Umiarkowany' },
  { value: 'lekki', label: 'Lekki' },
];

const DISABILITY_SYMBOLS = [
  '01-U', '02-P', '03-L', '04-O', '05-R', '06-E', '07-S', '08-T', '09-M', '10-N', '11-I', '12-C',
];

const FUNDING_SOURCES = [
  { value: 'pfron', label: 'PFRON' },
  { value: 'pcpr', label: 'PCPR / MOPR' },
  { value: 'nfz', label: 'NFZ' },
  { value: 'wlasny', label: 'Środki własne' },
  { value: 'inne', label: 'Inne' },
];

export const TurnusyMedicalPanel: React.FC<Props> = ({ application, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    disability_level: application.disability_level || '',
    disability_symbols: application.disability_symbols || [],
    wheelchair: application.wheelchair || false,
    guardian_required: application.guardian_required || false,
    guardian_name: application.guardian_name || '',
    special_diet: application.special_diet || '',
    medical_notes: application.medical_notes || '',
    funding_source: application.funding_source || '',
    pfron_decision_number: application.pfron_decision_number || '',
    pfron_decision_date: application.pfron_decision_date || '',
    orzeczenie_number: application.orzeczenie_number || '',
    orzeczenie_valid_until: application.orzeczenie_valid_until || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/crm/applications/${application.id}`, {
        disability_level: form.disability_level || null,
        disability_symbols: form.disability_symbols.length > 0 ? form.disability_symbols : null,
        wheelchair: form.wheelchair,
        guardian_required: form.guardian_required,
        guardian_name: form.guardian_name || null,
        special_diet: form.special_diet || null,
        medical_notes: form.medical_notes || null,
        funding_source: form.funding_source || null,
        pfron_decision_number: form.pfron_decision_number || null,
        pfron_decision_date: form.pfron_decision_date || null,
        orzeczenie_number: form.orzeczenie_number || null,
        orzeczenie_valid_until: form.orzeczenie_valid_until || null,
      });
      onSaved();
    } catch { /* */ }
    setSaving(false);
  };

  const toggleSymbol = (s: string) => {
    setForm(f => ({
      ...f,
      disability_symbols: f.disability_symbols.includes(s)
        ? f.disability_symbols.filter((x: string) => x !== s)
        : [...f.disability_symbols, s],
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-5 py-3 flex items-center gap-2">
        <Heart size={18} className="text-white" />
        <h3 className="text-white font-bold text-sm">Profil medyczny / Turnus rehabilitacyjny</h3>
      </div>
      <div className="p-5 space-y-4">
        {/* Disability level */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Stopień niepełnosprawności</label>
          <div className="flex gap-2">
            {DISABILITY_LEVELS.map(d => (
              <button
                key={d.value}
                onClick={() => setForm(f => ({ ...f, disability_level: f.disability_level === d.value ? '' : d.value }))}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  form.disability_level === d.value
                    ? 'bg-rose-600 text-white border-rose-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-rose-300'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Disability symbols */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Symbole przyczyny niepełnosprawności</label>
          <div className="flex flex-wrap gap-1.5">
            {DISABILITY_SYMBOLS.map(s => (
              <button
                key={s}
                onClick={() => toggleSymbol(s)}
                className={`px-2 py-1 rounded text-xs font-mono border transition-colors ${
                  form.disability_symbols.includes(s)
                    ? 'bg-rose-100 text-rose-700 border-rose-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-rose-200'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Checkboxes */}
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.wheelchair} onChange={e => setForm(f => ({ ...f, wheelchair: e.target.checked }))} className="rounded text-rose-600 focus:ring-rose-500" />
            Wózek inwalidzki
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.guardian_required} onChange={e => setForm(f => ({ ...f, guardian_required: e.target.checked }))} className="rounded text-rose-600 focus:ring-rose-500" />
            Wymaga opiekuna
          </label>
        </div>

        {form.guardian_required && (
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Imię i nazwisko opiekuna</label>
            <input type="text" value={form.guardian_name} onChange={e => setForm(f => ({ ...f, guardian_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Jan Kowalski" />
          </div>
        )}

        {/* Orzeczenie */}
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div className="flex items-center gap-1.5 mb-2">
            <FileCheck size={14} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-700">Orzeczenie o niepełnosprawności</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Nr orzeczenia</label>
              <input type="text" value={form.orzeczenie_number} onChange={e => setForm(f => ({ ...f, orzeczenie_number: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Ważne do</label>
              <input type="date" value={form.orzeczenie_valid_until} onChange={e => setForm(f => ({ ...f, orzeczenie_valid_until: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
          </div>
          {form.orzeczenie_valid_until && new Date(form.orzeczenie_valid_until) < new Date() && (
            <div className="flex items-center gap-1 mt-2 text-xs text-red-600">
              <AlertTriangle size={12} />
              Orzeczenie wygasło!
            </div>
          )}
        </div>

        {/* Funding */}
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <label className="text-xs font-bold text-blue-700 mb-2 block">Źródło dofinansowania</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {FUNDING_SOURCES.map(f => (
              <button
                key={f.value}
                onClick={() => setForm(prev => ({ ...prev, funding_source: prev.funding_source === f.value ? '' : f.value }))}
                className={`px-3 py-1 rounded text-xs border transition-colors ${
                  form.funding_source === f.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {(form.funding_source === 'pfron' || form.funding_source === 'pcpr') && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-xs text-gray-500">Nr decyzji PFRON/PCPR</label>
                <input type="text" value={form.pfron_decision_number} onChange={e => setForm(f => ({ ...f, pfron_decision_number: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Data decyzji</label>
                <input type="date" value={form.pfron_decision_date} onChange={e => setForm(f => ({ ...f, pfron_decision_date: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
              </div>
            </div>
          )}
        </div>

        {/* Diet & Medical notes */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Dieta specjalna</label>
          <input type="text" value={form.special_diet} onChange={e => setForm(f => ({ ...f, special_diet: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="np. bezglutenowa, wegetariańska..." />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Uwagi medyczne</label>
          <textarea value={form.medical_notes} onChange={e => setForm(f => ({ ...f, medical_notes: e.target.value }))} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Alergie, leki, wymagania specjalne..." />
        </div>

        <button onClick={handleSave} disabled={saving} className="w-full bg-rose-600 text-white py-2 rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Zapisywanie...' : 'Zapisz profil medyczny'}
        </button>
      </div>
    </div>
  );
};
